import { endpoints, oids } from "@/configs/Settings";
import { GetQuestionnaireResponse } from "@/services/fhir/patientsummary/questionnaireResponse/GetQuestionnaireResponse";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { GetAppointment, GetPatient } from "@/services/fhir";
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { Patient } from "fhir/r5";
import { GetVetPatient } from "@/services/fhir/vetpatient/GetVetPatient";
import { useSessionContext } from "@/contexts/SessionContext";
import {
  extractLatestDiagnosticSuspicions,
  extractLatestClinicalConducts,
} from "@/fluxvet/utils/questionnaireResponseHelper";
import { convertFromQuestionnaireResponse } from "@/fluxvet/models/questionnaire-mapper";
import type { VetProtocolQuestionnaireItem } from "@/fluxvet/hooks/usePrescriptionTemplates";

// =============================================================================
// TIPOS - Interface completa do formulário de atendimento
// =============================================================================

/**
 * Interface completa dos dados do atendimento veterinário
 * Inclui todas as etapas: Coleta de dados, Prescrições e Assinatura
 */
export interface IVetAttendanceForm {
  // ===== ETAPA 1: COLETA DE DADOS =====
  // Queixa Principal
  mainComplaint: string;

  // Anamnese - Texto livre
  anamnesis?: string;

  // Anamnese - Sistemas Corporais
  cardiorespiratory?: string[]; // Eupneia, Bradpneia, Taquipneia, Dispneia
  cardiorespiratoryOther?: string; // Campo livre para outras observações
  waterIntake?: string; // Normodipsia, Polipsia, Oligodipsia, Adipsia
  digestory?: string[]; // Normorexia, Hiporexia, Anorexia, Polifagia, Coprofagia, Disfagia, Apetite seletivo, Êmese, Regurgitação
  digestoryOther?: string; // Campo livre para outras observações
  defecation?: string[]; // Normoquezia, Aquezia, Disquezia, Tenesmo, Diarreia, Hematoquezia, Melena
  defecationOther?: string; // Campo livre para outras observações
  urogenital?: string; // "Secreção" ou "Sem secreção"
  urogenitalDescription?: string; // Descrição adicional
  deworming?: string; // "Sim" ou "Não"
  dewormingDescription?: string; // Descrição da vermifugação
  feedingType?: string[]; // Petiscos, Ração Úmida, Legumes/Vegetais, A. Natural
  feedingTypeDescription?: string; // Descrição adicional da alimentação
  anamnesisObservations?: string; // Observações gerais da anamnese
  neurological?: string[]; // Inclinação da cabeça, Crise Epiléptica, Ataxia, Letargia
  exerciseIntolerance?: string[]; // Fadiga, Síncope, Cianose
  females?: string[]; // Galactorreia, Pseudociese, Alt. de ciclo estral

  // Exame Físico
  physicalExam?: string;

  // Sinais Vitais
  temperature?: number; // Temperatura em °C
  heartRate?: number; // FC - Frequência Cardíaca em bpm
  respiratoryRate?: number; // FR - Frequência Respiratória em mpm
  capillaryRefillTime?: number; // TPC - Tempo de Preenchimento Capilar em segundos
  hydration?: string; // Estado de hidratação
  weight?: number; // Peso em kg

  // Exame Físico Detalhado
  lymphNodes?: string; // Linfonodos (palpação)
  cardiacPulmonaryAuscultation?: string; // Ausculta cardíaca e pulmonar
  eyesMucosa?: string; // Olhos e mucosa ocular
  oralMucosa?: string; // Boca e mucosa oral
  earCanals?: string; // Condutos auditivos
  skin?: string; // Descrição da pele
  skinConditions?: string[]; // Condições dermatológicas (checkboxes)
  skinOther?: string; // Outras observações sobre pele
  locomotorSystem?: string; // Aparelho locomotor
  anteriorLimbs?: string; // Membros anteriores
  posteriorLimbs?: string; // Membros posteriores

  // Histórico Clínico
  continuousMedication?: string;
  recentExams?: string;
  streetAccess?: boolean;
  streetAccessDescription?: string;
  feeding?: string;
  vaccination?: boolean; // Vacinação (sim/não)
  vaccinationDescription?: string; // Descrição da vacinação
  ectoparasiteControl?: boolean; // Controle de ectoparasitas (sim/não)
  ectoparasiteControlDescription?: string; // Descrição do controle de ectoparasitas

  // Observações
  observations?: string;

  // Diagnósticos e Condutas
  diagnosticSuspicions?: Array<{
    id: string;
    description: string;
    status?: "confirmed" | "not_confirmed"; // Status da suspeita: confirmado ou não confirmado
    confirmationDescription?: string; // Descrição adicional (sempre disponível)
  }>; // Múltiplas suspeitas diagnósticas
  clinicalConducts?: Array<{
    id: string;
    description: string;
    status?: "success" | "failed" | "in_progress"; // Status da conduta: sucesso, não teve sucesso ou em andamento
    outcomeDescription?: string; // Descrição do resultado/desfecho da conduta (sempre disponível)
  }>; // Múltiplas condutas clínicas

  // ===== ETAPA 2: PRESCRIÇÕES =====
  // Tratamentos (Medicamentos)
  treatments?: Array<{
    id: string;
    name: string;
    dosage: string;
    description: string;
    signed?: boolean; // Indica se o item foi assinado digitalmente
  }>;

  // Exames ou Procedimentos
  examsOrProcedures?: Array<{
    id: string;
    description: string;
    notes: string;
    signed?: boolean; // Indica se o item foi assinado digitalmente
  }>;

  // Relatórios
  reports?: Array<{
    id: string;
    content: string;
    signed?: boolean; // Indica se o item foi assinado digitalmente
  }>;

  // Orientações
  guidelines?: string;

  /**
   * Questionários (ex.: pós-consulta) escolhidos para envio ao tutor/pet via tasks,
   * alinhado ao fluxo humano (`type: "questionnaire"`).
   */
  selectedQuestionnaires?: VetProtocolQuestionnaireItem[];

  // ===== ETAPA 3: ASSINATURA =====
  // Documentos assinados digitalmente
  signedDocuments?: Array<{
    uuid: string;
    title: string;
    filename: string;
    base64: string;
    signedAt: string;
    type: "treatment" | "exam" | "report" | "combined";
    itemIds: string[]; // IDs dos itens incluídos no documento
  }>;
}

// =============================================================================
// AJUDA — Bundle FHIR (Appointment + Location _include)
// =============================================================================

/** Lê `attendance-channel` na Location referenciada no Appointment (requer includeLocation na busca). */
function extractLocationAttendanceChannelFromBundle(
  appointmentResource: any,
  bundleEntries: any[] | undefined,
): string | null {
  if (!appointmentResource?.participant || !bundleEntries?.length) {
    return null;
  }

  const locParticipant = appointmentResource.participant.find((p: any) =>
    p.actor?.reference?.startsWith?.("Location/"),
  );
  if (!locParticipant?.actor?.reference) {
    return null;
  }

  const rawRef = locParticipant.actor.reference as string;
  const locId = rawRef.replace(/^Location\//, "").split("/")[0];

  const locationRes = bundleEntries.find((e: any) => {
    if (e.resource?.resourceType !== "Location") return false;
    const id = e.resource?.id as string | undefined;
    return id === locId || rawRef.endsWith(id || "");
  })?.resource;

  const channel = locationRes?.extension?.find((ext: any) =>
    ext.url?.includes?.("attendance-channel"),
  )?.valueCode;

  return typeof channel === "string" ? channel : null;
}

// =============================================================================
// INTERFACE DO CONTEXTO
// =============================================================================

interface VetAttendanceContextData {
  methods: UseFormReturn<IVetAttendanceForm>;
  loadingQuestionnaireResponse: boolean;
  historyQuestionnaireResponse: any[];
  questionnaireDefualt: any;
  isFormDirty: boolean;
  isEditMode: boolean;
  appointment: fhir5.Appointment | null;
  petData: Patient | null;
  tutorData: Patient | null;
  /** Canal da Location do agendamento (ex.: chat, domicilio). Derivado do bundle com includeLocation. */
  locationAttendanceChannel: string | null;

  attendanceId?: string;
  resetForm: () => void;
  saveData: () => void;
  loadAttendanceData: (data: Partial<IVetAttendanceForm>, id?: string) => void;
}

// =============================================================================
// CRIAÇÃO DO CONTEXTO
// =============================================================================

const VetAttendanceContext = createContext<VetAttendanceContextData>(
  {} as VetAttendanceContextData
);

// =============================================================================
// PROVIDER DO CONTEXTO
// =============================================================================

interface VetAttendanceProviderProps {
  children: ReactNode;
  initialData?: Partial<IVetAttendanceForm>;
  attendanceId?: string;
}

/**
 * Provider do contexto de atendimento veterinário
 * Gerencia o estado do formulário de todas as etapas
 * Mantém os dados persistentes ao trocar entre etapas
 *
 * @param initialData - Dados iniciais para carregar (caso seja edição)
 * @param attendanceId - ID do atendimento sendo editado
 */
export const VetAttendanceProvider: React.FC<VetAttendanceProviderProps> = ({
  children,
  initialData,
  attendanceId,
}) => {
  const { user } = useSessionContext();
  const { state } = useLocation();

  const [tutorData, setTutorData] = useState<Patient | null>(null);
  const [petData, setPetData] = useState<Patient | null>(null);
  const [appointment, setAppointment] = useState<fhir5.Appointment | null>(
    null
  );
  /** attendance-channel da Location do appointment (populate via GetAppointment includeLocation). */
  const [locationAttendanceChannel, setLocationAttendanceChannel] = useState<
    string | null
  >(null);

  const [historyQuestionnaireResponse, setHistoryQuestionnaireResponse] =
    useState<any[]>([]);

  const questionnaireDefualt = historyQuestionnaireResponse?.find(
    (attendance: any) =>
      attendance?.encounter?.reference?.split("/").pop() ===
      state?.encounterReference?.id
  );

  const [loadingQuestionnaireResponse, setLoadingQuestionnaireResponse] =
    useState<boolean>(false);
  // Valores padrão para novo atendimento
  const defaultValues: IVetAttendanceForm = {
    // Etapa 1: Coleta de dados
    mainComplaint: "",
    anamnesis: "",
    cardiorespiratory: [],
    cardiorespiratoryOther: "",
    waterIntake: "",
    digestory: [],
    digestoryOther: "",
    defecation: [],
    defecationOther: "",
    urogenital: "",
    urogenitalDescription: "",
    deworming: "",
    dewormingDescription: "",
    feedingType: [],
    feedingTypeDescription: "",
    anamnesisObservations: "",
    neurological: [],
    exerciseIntolerance: [],
    females: [],
    physicalExam: "",
    temperature: undefined,
    heartRate: undefined,
    respiratoryRate: undefined,
    capillaryRefillTime: undefined,
    hydration: "",
    weight: undefined,
    lymphNodes: "",
    cardiacPulmonaryAuscultation: "",
    eyesMucosa: "",
    oralMucosa: "",
    earCanals: "",
    skin: "",
    skinConditions: [],
    skinOther: "",
    locomotorSystem: "",
    anteriorLimbs: "",
    posteriorLimbs: "",
    continuousMedication: "",
    recentExams: "",
    streetAccess: false,
    streetAccessDescription: "",
    feeding: "",
    vaccination: false,
    vaccinationDescription: "",
    ectoparasiteControl: false,
    ectoparasiteControlDescription: "",
    observations: "",
    diagnosticSuspicions: [],
    clinicalConducts: [],

    // Etapa 2: Prescrições
    treatments: [],
    examsOrProcedures: [],
    reports: [],
    guidelines: "",
    selectedQuestionnaires: [],

    // Etapa 3: Assinatura
    signedDocuments: [],
  };

  /**
   * Busca todos os dados necessários para o atendimento veterinário
   * Inclui: Appointment, Pet, Tutor, RelatedPerson e histórico de questionários
   * Usa Promise.all para otimizar as chamadas em paralelo
   */
  const fetchAttendanceData = useCallback(async () => {
    setLoadingQuestionnaireResponse(true);
    try {
      // Passo 1: Busca dados iniciais em paralelo (não dependem uns dos outros)
      const [
        appointmentResponse,
        questionnaireResponse,
        petDataResponse,
        relatedPersonResponse,
      ] = await Promise.all([
        // Busca o appointment
        GetAppointment({
          _id: state?.encounterReference?.appointmentId,
          includePatient: true,
          /** Necessário para saber se o slot é chat / domicilio / tele na visão profissional (AttendanceHeader). */
          includeLocation: true,
          actor: `Practitioner/${oids.cpf}-${user.username}`,
        }),
        // Busca o histórico de questionários do pet
        GetQuestionnaireResponse({
          patient: state?.encounterReference?.subject,
          questionnaire: endpoints.QUESTIONNARIE_ID,
        }),
        // Busca os dados completos do pet
        GetVetPatient({
          id: state?.encounterReference?.subject,
        }),
        // Busca o RelatedPerson vinculado ao pet
        GetRelatedPerson({
          patient: state?.encounterReference?.subject,
        }),
      ]);

      // Processa e seta os dados do appointment (+ canal da Location, se vier no bundle)
      const auxAppointment = appointmentResponse?.entry?.find(
        (el: any) => el.resource.resourceType === "Appointment"
      );
      const aptResource = auxAppointment?.resource || null;
      setAppointment(aptResource);
      setLocationAttendanceChannel(
        aptResource
          ? extractLocationAttendanceChannelFromBundle(
              aptResource,
              appointmentResponse?.entry,
            )
          : null,
      );

      // Seta os dados do pet
      setPetData(petDataResponse?.data || null);

      // Seta o histórico de questionários
      setHistoryQuestionnaireResponse(
        questionnaireResponse?.entry?.map((item: any) => item.resource) || []
      );

      // Passo 2: Busca dados do tutor (depende do RelatedPerson)
      const relatedPersonData = relatedPersonResponse?.data;

      if (relatedPersonData?.entry && relatedPersonData.entry.length > 0) {
        const relatedPerson = relatedPersonData.entry[0].resource;
        const tutorIdentifier = relatedPerson?.identifier?.[0]?.value;

        if (tutorIdentifier) {
          // Busca os dados completos do tutor usando o identifier (CPF)
          const tutorPatientData = await GetPatient({
            identifier: tutorIdentifier,
          });

          // Seta o tutorData com a resposta do GetPatient
          setTutorData(tutorPatientData?.data || null);
        } else {
          console.warn("Identifier do tutor não encontrado no RelatedPerson");
          setTutorData(null);
        }
      } else {
        console.warn("Nenhum RelatedPerson encontrado para o pet");
        setTutorData(null);
      }
    } catch (error) {
      // Em caso de erro, reseta todos os estados
      setAppointment(null);
      setLocationAttendanceChannel(null);
      setPetData(null);
      setHistoryQuestionnaireResponse([]);
      setTutorData(null);
      console.error("Erro ao carregar dados do atendimento:", error);
    } finally {
      setLoadingQuestionnaireResponse(false);
    }
  }, [
    state?.encounterReference?.subject,
    state?.encounterReference?.appointmentId,
    user.username,
  ]);

  // Extrai as suspeitas diagnósticas mais recentes do histórico
  // Usa useMemo para recalcular apenas quando o histórico mudar
  const latestDiagnosticSuspicions = useMemo(() => {
    if (
      historyQuestionnaireResponse.length === 0 ||
      (initialData && initialData.diagnosticSuspicions)
    ) {
      // Se não há histórico ou já tem dados iniciais, não preenche
      return undefined;
    }
    return extractLatestDiagnosticSuspicions(historyQuestionnaireResponse);
  }, [historyQuestionnaireResponse, initialData]);

  // Extrai as condutas clínicas mais recentes do histórico
  // Usa useMemo para recalcular apenas quando o histórico mudar
  const latestClinicalConducts = useMemo(() => {
    if (
      historyQuestionnaireResponse.length === 0 ||
      (initialData && initialData.clinicalConducts)
    ) {
      // Se não há histórico ou já tem dados iniciais, não preenche
      return undefined;
    }
    return extractLatestClinicalConducts(historyQuestionnaireResponse);
  }, [historyQuestionnaireResponse, initialData]);

  // Prepara os valores padrão mesclando com dados iniciais
  // As suspeitas mais recentes serão adicionadas via useEffect após o histórico ser carregado
  const finalDefaultValues = useMemo(() => {
    let values = { ...defaultValues };

    // Se houver initialData, mescla primeiro
    if (initialData) {
      values = { ...values, ...initialData };
    }

    return values;
  }, [initialData]);

  // Configuração do react-hook-form para todas as etapas
  const methods = useForm<IVetAttendanceForm>({
    mode: "onChange",
    defaultValues: finalDefaultValues,
  });

  const { formState, reset } = methods;

  // Atualiza o formulário quando as suspeitas mais recentes forem carregadas
  // Isso é necessário porque o histórico pode ser carregado após a inicialização do formulário
  // Não executa se houver questionnaireDefualt (modo edição), pois os dados virão dele
  useEffect(() => {
    // Só executa quando o carregamento terminar e houver suspeitas para carregar
    // Não executa se houver questionnaireDefualt (modo edição)
    if (
      !loadingQuestionnaireResponse &&
      latestDiagnosticSuspicions &&
      latestDiagnosticSuspicions.length > 0 &&
      !initialData?.diagnosticSuspicions &&
      !questionnaireDefualt // Não carrega do histórico se estiver editando
    ) {
      // Verifica se o formulário ainda não tem suspeitas preenchidas
      const currentSuspicions = methods.getValues("diagnosticSuspicions");
      if (!currentSuspicions || currentSuspicions.length === 0) {
        // Atualiza apenas o campo de suspeitas, mantendo outros valores
        // Os dados já vêm no formato correto da função extractLatestDiagnosticSuspicions
        methods.setValue("diagnosticSuspicions", latestDiagnosticSuspicions, {
          shouldDirty: false, // Não marca como dirty ao carregar dados do histórico
          shouldValidate: false, // Não valida ao carregar dados do histórico
        });
      }
    }
  }, [
    latestDiagnosticSuspicions,
    initialData,
    loadingQuestionnaireResponse,
    questionnaireDefualt,
    methods,
  ]);

  // Atualiza o formulário quando as condutas mais recentes forem carregadas
  // Isso é necessário porque o histórico pode ser carregado após a inicialização do formulário
  // Não executa se houver questionnaireDefualt (modo edição), pois os dados virão dele
  useEffect(() => {
    // Só executa quando o carregamento terminar e houver condutas para carregar
    // Não executa se houver questionnaireDefualt (modo edição)
    if (
      !loadingQuestionnaireResponse &&
      latestClinicalConducts &&
      latestClinicalConducts.length > 0 &&
      !initialData?.clinicalConducts &&
      !questionnaireDefualt // Não carrega do histórico se estiver editando
    ) {
      // Verifica se o formulário ainda não tem condutas preenchidas
      const currentConducts = methods.getValues("clinicalConducts");
      if (!currentConducts || currentConducts.length === 0) {
        // Atualiza apenas o campo de condutas, mantendo outros valores
        // Os dados já vêm no formato correto da função extractLatestClinicalConducts
        methods.setValue("clinicalConducts", latestClinicalConducts, {
          shouldDirty: false, // Não marca como dirty ao carregar dados do histórico
          shouldValidate: false, // Não valida ao carregar dados do histórico
        });
      }
    }
  }, [
    latestClinicalConducts,
    initialData,
    loadingQuestionnaireResponse,
    questionnaireDefualt,
    methods,
  ]);

  // Carrega todos os dados do questionnaireDefualt quando estiver editando um atendimento
  // Isso preenche o formulário completo com os dados do QuestionnaireResponse
  useEffect(() => {
    // Só executa quando houver questionnaireDefualt (modo edição) e não houver initialData
    // Se houver initialData, significa que os dados já foram carregados de outra forma
    if (!loadingQuestionnaireResponse && questionnaireDefualt && !initialData) {
      try {
        // Converte o QuestionnaireResponse para dados do formulário
        const formDataFromResponse =
          convertFromQuestionnaireResponse(questionnaireDefualt);

        // Mescla com os valores padrão para garantir que todos os campos existam
        const mergedData = { ...defaultValues, ...formDataFromResponse };

        // Reseta o formulário com os dados convertidos
        // shouldDirty: false para não marcar como modificado ao carregar
        reset(mergedData);
      } catch (error) {
        console.error(
          "Erro ao converter QuestionnaireResponse para dados do formulário:",
          error
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireDefualt, loadingQuestionnaireResponse, initialData, reset]);

  /**
   * Verifica se o formulário foi modificado
   */
  const isFormDirty = formState.isDirty;

  /**
   * Indica se está em modo de edição (com attendanceId ou initialData)
   */
  const isEditMode = !!(attendanceId || initialData);

  /**
   * Reseta o formulário para valores padrão
   */
  const resetForm = () => {
    reset(defaultValues);
  };

  /**
   * Carrega dados de um atendimento existente no formulário
   * Útil para reabrir e editar atendimentos
   *
   * @param data - Dados parciais do atendimento a carregar
   * @param id - ID do atendimento (opcional)
   */
  const loadAttendanceData = (
    data: Partial<IVetAttendanceForm>,
    id?: string
  ) => {
    console.log("Carregando dados do atendimento:", id, data);
    // Mescla os dados carregados com os valores padrão
    const mergedData = { ...defaultValues, ...data };
    // Usa reset() para atualizar todos os campos do formulário
    reset(mergedData);
  };

  useEffect(() => {
    if (state?.encounterReference?.subject) {
      fetchAttendanceData();
    }
  }, [fetchAttendanceData]);
  /**
   * Salva os dados do atendimento
   * TODO: Implementar lógica de salvamento (API, localStorage, etc.)
   */
  const saveData = () => {
    const data = methods.getValues();
    console.log(
      isEditMode
        ? `Atualizando atendimento ${attendanceId}:`
        : "Criando novo atendimento:",
      data
    );
    // TODO: Enviar para API ou salvar no localStorage
    // Se isEditMode: PUT/PATCH para atualizar
    // Se novo: POST para criar
  };

  return (
    <VetAttendanceContext.Provider
      value={{
        methods,
        questionnaireDefualt,
        loadingQuestionnaireResponse,
        historyQuestionnaireResponse,
        isFormDirty,
        isEditMode,
        attendanceId,
        appointment,
        locationAttendanceChannel,
        petData,
        tutorData,
        resetForm,
        saveData,
        loadAttendanceData,
      }}
    >
      {children}
    </VetAttendanceContext.Provider>
  );
};

// =============================================================================
// HOOK CUSTOMIZADO
// =============================================================================

/**
 * Hook para acessar o contexto de atendimento veterinário
 * @returns Contexto com métodos do formulário e funções auxiliares
 */
export const useVetAttendance = (): VetAttendanceContextData => {
  const context = useContext(VetAttendanceContext);

  if (!context) {
    throw new Error(
      "useVetAttendance deve ser usado dentro de VetAttendanceProvider"
    );
  }

  return context;
};

export default VetAttendanceContext;
